/**
 * Reveal - small wrapper around `useReveal` so sections stay declarative.
 *
 * Usage:
 *   <Reveal delay={120}>...</Reveal>            -> y-translate fade
 *   <Reveal kind="reveal-x-left">...</Reveal>   -> slide in from left
 *   <Reveal kind="reveal-soft">...</Reveal>     -> shorter, softer rise
 */
import type { ReactNode, CSSProperties } from "react";
import { useReveal } from "../lib/useReveal";

type Kind = "reveal" | "reveal-soft" | "reveal-x-left" | "reveal-x-right";

interface RevealProps {
  children: ReactNode;
  kind?: Kind;
  delay?: number;
  className?: string;
  style?: CSSProperties;
  as?: "div" | "section" | "article" | "header" | "footer";
}

export default function Reveal({
  children,
  kind = "reveal",
  delay = 0,
  className = "",
  style,
  as: Tag = "div",
}: RevealProps) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <Tag
      ref={ref as never}
      className={`${kind} ${className}`}
      style={{ ["--d" as never]: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
}
